import { EventMessage, EventType, PublicClientApplication } from "@azure/msal-browser";
import jwt_decode from "jwt-decode";
import { loginRequest, msalConfig } from "../../AuthConfig";
import { appDomain, appEmit } from "../../domain/AppDomain";
import { EventNames } from "../../domain/events/Events";
import { IToken } from "../../domain/authentication/IToken";
import { IClaims } from "../../domain/authentication/IClaims";
import { IAuthenticationRepository } from "../../domain/authentication/IAuthenticationRepository";

export const requiresInteraction = (errorMessage: string) => {
    if (!errorMessage || !errorMessage.length) {
        return false;
    }

    return (
        errorMessage.indexOf("consent_required") > -1 ||
        errorMessage.indexOf("interaction_required") > -1 ||
        errorMessage.indexOf("login_required") > -1
    );
};

export class AuthenticationRepository implements IAuthenticationRepository {
    public msalInstance: PublicClientApplication;
    public scopes = { scopes: loginRequest.scopes };

    constructor() {
        this.msalInstance = new PublicClientApplication(msalConfig);

        this.msalInstance.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                appEmit(EventNames.loggedIn);
            }
            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                appEmit(EventNames.loggedOut);
            }
        });
    }

    getUserEmail(): string {
        return this.getClaims().preferred_username;
    }

    getUserName(): string {
        return this.getClaims().name;
    }

    getClaims(): IClaims {
        const token = appDomain.IAuthenticationService.getToken();
        if (token) {
            return jwt_decode(token) as IClaims;
        }
        return {} as IClaims;
    }

    logout() {
        this.msalInstance.logoutRedirect();
    }

    isAuthenticated(): boolean {
        const activeAccount = this.msalInstance.getActiveAccount();
        return activeAccount != null;
    }

    async getTokenFromTokenProvider(): Promise<IToken> {
        if (!this.isAuthenticated()) {
            return { token: "" };
        }

        const loginResponse = await this.msalInstance.acquireTokenSilent(this.scopes);
        return { token: loginResponse.accessToken };
    }

    async loginRedirect() {
        const redirectPromise = await this.msalInstance.handleRedirectPromise();

        if (redirectPromise != null) {
            this.msalInstance.setActiveAccount(redirectPromise.account);
        }

        return this.msalInstance
            .acquireTokenSilent(this.scopes)
            .then((loginResponse: any) => {
                if (loginResponse) {
                    appDomain.IAuthenticationService.saveToken({ token: loginResponse.accessToken } as IToken);
                    return true;
                }
            })
            .catch((error: any) => {
                if (requiresInteraction(error.errorCode)) {
                    return this.onSignIn(this.scopes);
                }
                return this.onSignIn(this.scopes);
            });
    }

    async onSignIn(request: { scopes: string[] }) {
        await this.msalInstance.loginRedirect({
            ...request,
            extraQueryParameters: {
                ui_locales: localStorage.getItem("language") || "nl"
            }
        });
    }
}
